import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiPlus, FiX, FiMessageCircle } = FiIcons;

const ManageFeedbackQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [formData, setFormData] = useState({ question: '' });

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await apiService.getFeedbackQuestions();
      if (response.status === 'success') {
        setQuestions(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching feedback questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentQuestion) {
        await apiService.updateFeedbackQuestion(currentQuestion.id, { question: formData.question });
      } else {
        await apiService.createFeedbackQuestion({ question: formData.question });
      }
      
      await fetchQuestions();
      closeModal();
    } catch (error) {
      console.error('Error saving feedback question:', error);
      alert('Fehler beim Speichern der Feedback-Frage. Bitte versuchen Sie es erneut.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Feedback-Frage wirklich löschen?')) {
      try {
        await apiService.deleteFeedbackQuestion(id);
        await fetchQuestions();
      } catch (error) {
        console.error('Error deleting feedback question:', error);
        alert('Fehler beim Löschen der Feedback-Frage.');
      }
    }
  };

  const openModal = (question = null) => {
    if (question) {
      setCurrentQuestion(question);
      setFormData({ question: question.question });
    } else {
      setCurrentQuestion(null);
      setFormData({ question: '' });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentQuestion(null);
    setFormData({ question: '' });
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-brand-dark">Feedback-Fragen verwalten</h2>
          <p className="text-sm text-gray-500 mt-1">
            Diese Fragen werden für das gegenseitige Feedback im Team verwendet.
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark transition-colors shadow-sm"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Neue Frage
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        {questions.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <SafeIcon icon={FiMessageCircle} className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>Es wurden noch keine Feedback-Fragen angelegt.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {questions.map((q) => (
              <li key={q.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-start">
                   <div className="mt-1 mr-3 flex-shrink-0">
                     <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-brand-secondary" />
                   </div>
                   <span className="text-gray-900 font-medium">{q.question}</span>
                </div>
                <div className="flex space-x-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => openModal(q)}
                    className="text-gray-500 hover:text-brand-secondary p-2 rounded-full hover:bg-brand-secondary/10 transition-colors"
                    title="Bearbeiten"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(q.id)}
                    className="text-gray-500 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors"
                    title="Löschen"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900">
                {currentQuestion ? 'Frage bearbeiten' : 'Neue Feedback-Frage'}
              </h3>
              <button 
                onClick={closeModal} 
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fragetext
                </label>
                <textarea
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-shadow"
                  rows="4"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="z.B. Wie bewerten Sie die Kommunikationsfähigkeit?"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Diese Frage wird den Teammitgliedern mit einer Skala von 1-5 zur Bewertung angezeigt.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark font-medium text-sm transition-colors shadow-sm"
                >
                  {currentQuestion ? 'Änderungen speichern' : 'Frage erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageFeedbackQuestions;